import { useState } from "react";
import {
  MODEL_GAINS,
  HIGHRES_METHOD,
  HIGHRES_WHY,
  type ModelGain,
} from "../../lib/modelGains";
import { DialogHeader, Popup } from "../../ui/Popup";

// The (i) on the Map-Soil High-Res option opens this instead of a plain tooltip because the copy is
// a full panel: a prominent "Compare to", the Big-Data pitch + what the 5 models do, then a
// per-model selector whose card shows the interpolation method + the STATIC gain breakdown (from
// lib/modelGains — computed from each model's internals report; see scratchpad/compute_model_gains.

export const HIGHRES_COMPARE_TO = "Advanced Agrilytics, EarthOptics, Senus";
export const HIGHRES_INTRO =
  "Enrich soil sample data with AI & up to 6,000 field-specific data layers including waterflow, topography, soil texture, soil taxonomy, vegetation indices, soil color, crop history, cover crop status, precipitation, temperature, etc";
export const HIGHRES_BULLETS: string[] = [];

export function HighResInfo({
  onClose,
  initialModel,
}: {
  onClose: () => void;
  initialModel?: string | null;
}) {
  const [modelKey, setModelKey] = useState<string | null>(initialModel ?? null);
  const model = MODEL_GAINS.find((m) => m.key === modelKey) ?? null;
  return (
    <Popup
      onClose={onClose}
      className="flex max-h-[88vh] w-full max-w-xl flex-col rounded-lg bg-surface text-fg shadow-2xl"
    >
      <DialogHeader title="Genius Map" onClose={onClose} />
      <div className="overflow-auto p-4 text-[12px] leading-snug">
        {/* Compare to — prominent, at the very top (v2 rule for every info box) */}
        <div className="mb-3 rounded border border-accent/40 bg-accent/5 px-2.5 py-1.5 text-[11px]">
          <span className="font-semibold uppercase tracking-wide text-accent">
            Compare to:
          </span>{" "}
          <span className="font-medium">{HIGHRES_COMPARE_TO}</span>
        </div>

        <p className="text-fg">{HIGHRES_INTRO}</p>

        <ul className="mt-2 space-y-1 text-muted">
          {HIGHRES_BULLETS.map((b, i) => (
            <li key={i} className="flex gap-1.5">
              <span className="text-accent">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 border-t border-line pt-3">
          <p className="mb-1.5 font-semibold">Select a Model to Learn More:</p>
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-label="Soil-property model"
          >
            {MODEL_GAINS.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setModelKey(m.key === modelKey ? null : m.key)}
                aria-pressed={m.key === modelKey}
                className={`rounded border px-2.5 py-1 text-[12px] font-medium ${
                  m.key === modelKey
                    ? "border-accent bg-accent text-canvas"
                    : "border-line hover:bg-hover"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {model ? (
            <ModelCard model={model} />
          ) : (
            <p className="mt-2 text-[11px] text-faint">
              Pick a model above to see its interpolation method and how it
              weights each data category.
            </p>
          )}
        </div>
      </div>
    </Popup>
  );
}

function ModelCard({ model }: { model: ModelGain }) {
  const max = Math.max(...model.classes.map((c) => c.pct), 1);
  return (
    <div className="mt-3 rounded border border-line bg-raised p-3">
      <div className="mb-2">
        <p className="font-semibold text-muted">Interpolation Method</p>
        <p>{HIGHRES_METHOD}</p>
      </div>
      <div className="mb-2">
        <p className="font-semibold text-muted">Why This Method</p>
        <p>{HIGHRES_WHY}</p>
      </div>
      <p className="font-semibold text-muted">Relative Weights / Gain</p>
      <ul className="mt-1 space-y-1.5">
        {model.classes.map((c) => (
          <li key={c.cls}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium">{c.cls}</span>
              <span className="tnum whitespace-nowrap text-faint">
                {c.pct}% · {c.n} layer{c.n === 1 ? "" : "s"}
              </span>
            </div>
            <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded bg-hover">
              <div
                className="h-full rounded bg-accent"
                style={{ width: `${(c.pct / max) * 100}%` }}
              />
            </div>
            <div className="mt-0.5 text-[10px] text-faint">
              {c.subs.map((s) => `${s.sub} ${s.pct}%`).join(" · ")}
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] text-faint">
        Gain = share of total ensemble model gain; layers = feature count in
        that category. From the {model.label} model’s internals report (
        {model.nFeatures} features).
      </p>
    </div>
  );
}
